import React, { useState } from 'react';

interface StudentInfo {
  firstName: string;
  lastName: string;
  email: string;
  studentId: string;
}

interface EmailFormProps {
  onSubmit: (studentInfo: StudentInfo) => void;
}

const EmailForm: React.FC<EmailFormProps> = ({ onSubmit }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [studentId, setStudentId] = useState('');
  const [error, setError] = useState('');

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@packofparts\.org$/;
    return emailRegex.test(email);
  };

  const validateStudentId = (id: string): boolean => {
    const idRegex = /^\d{7}$/;
    return idRegex.test(id);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate all fields
    if (!firstName.trim()) {
      setError('First name is required');
      return;
    }

    if (!lastName.trim()) {
      setError('Last name is required');
      return;
    }

    if (!email.trim()) {
      setError('Email address is required');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid @packofparts.org email address');
      return;
    }

    if (!studentId.trim()) {
      setError('Student ID is required');
      return;
    }

    if (!validateStudentId(studentId)) {
      setError('Student ID must be exactly 7 digits');
      return;
    }

    onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim().toLowerCase(),
      studentId: studentId.trim()
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="card p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Robotics Safety Quiz
            </h1>
            <p className="text-gray-600">
              Complete all 10 sections with 100% accuracy to receive your safety certification
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safety-500 focus:border-safety-500"
                  placeholder="John"
                  required
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safety-500 focus:border-safety-500"
                  placeholder="Doe"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safety-500 focus:border-safety-500"
                placeholder="your.name@packofparts.org"
                required
              />
            </div>

            <div>
              <label htmlFor="studentId" className="block text-sm font-medium text-gray-700 mb-2">
                Student ID
              </label>
              <input
                type="text"
                id="studentId"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safety-500 focus:border-safety-500"
                placeholder="1234567"
                maxLength={7}
                pattern="\d{7}"
                required
              />
              <p className="mt-1 text-xs text-gray-500">Must be exactly 7 digits</p>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              className="w-full btn-primary"
            >
              Start Safety Quiz
            </button>
          </form>

          <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h3 className="font-medium text-yellow-800 mb-2">Quiz Requirements:</h3>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• Must use your @packofparts.org email address</li>
              <li>• Complete all 10 safety sections</li>
              <li>• Achieve 100% score in each section</li>
              <li>• Answer all questions then submit the entire quiz</li>
              <li>• Download your completion certificate</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailForm;